import chatMessage from './chatMessage';
import userModel from './user';
import { getChatMessageModel } from '../functions/messageUtils';
import { cleanObjectKeys } from '../functions/utils';
import { messageUtils, utils } from '../functions';
import { icMessGroup } from '../themes/ImagesSVG';
import { MEMBER_TYPES, PrimaryFilterType, SecondaryFilterType } from '../constants/constants';
import { IConversation } from '../interfaces/IConversation';
import User from './user';
import Folder from './Folder';
import ConversationStatus from './conversationStatus';

interface GroupDetail {
  body: {
    expts: number;
  };
  coverUrl: string;
  expiredAt: number;
  folders: any;
  id: number;
  inActiveRoomMembers: any[];
  isActive: boolean;
  isEnded: boolean;
  isExpired: boolean;
  isPrivate: boolean;
  msgId: any;
  msgXmppId: any;
  refObjId: number;
  refObjType: string;
  roomId: number;
  roomJid: string;
  roomMembers: any[];
  roomName: string;
  senderJid: any;
  subject: string;
  ts: number;
  userDelete: number;
  isMatching: boolean;
  memberLevel: number;
}

export interface ISearchConversation {
  keyword: string;
  primaryIndex: PrimaryFilterType;
  secondaryIndex: SecondaryFilterType;
  isSearched: boolean;
}

export interface IPeerVcard extends User {
  isGroup: boolean;
  roomId?: number;
  roomName?: string;
  isExpired: boolean;
  isExistsAvatar: boolean;
  dateFinderCount?: number;
  eventCount?: number;
  verificationState?: string;
}

export interface ISelectedConversationInfo extends ConversationStatus {
  isAPIDataLoaded: boolean;
  diffTime: number;
  diffTimeGroup: number;
  isLoading: boolean;
  isError: boolean;
  pinned: boolean;
  matched: boolean;
  editorStatus: number;
  remainingMessage: number;
  remainingCredits: number;
  canVideoCall: boolean;
  canSend: boolean;
  canSendPrivate: boolean;
  canSendPhotos: boolean;
  id: number | null;
  memberLevelUserLogin: number;
  isRecvMatching: boolean;
  isSenderMatching: boolean;
  isCreditSend: boolean;
  timeToReset: number;
  upgradeAction: string;
  conversationKey: string;
  timeUpdateConversationStatus: number;
  canSendGifts: boolean;
  msgIdSelected: string | null;
  isEditMessage: boolean;
  recvMemberLevel: number;
}

export interface IMatchingData {
  isRecvMatching: boolean;
  isSenderMatching: boolean;
  memberLevelUserLogin: number;
  recvMemberLevel: number;
}

export interface IpeerUserVideoCall extends IPeerVcard, IMatchingData {}

export default class Conversation implements IConversation {
  id: number;
  folders: number[];
  pinned: boolean;
  chatted: boolean;
  unreadCount: number;
  lastBuddySentTime: number;
  lastUserSentTime: number;
  message: any;
  peerVcard: IPeerVcard;
  timestamp: number;
  isActive: boolean;
  status: string;
  isGroup: boolean;
  memberCount: number;
  isNewInvitation: boolean;
  eventId: number;
  isIgnored: boolean | null;
  expiredTime: number;
  isDelete: boolean;
  hasUnreadGifts: boolean;
  memberLevel: number;
  giftLevel: number;
  isInboxRetention: boolean;
  buddyBoostExpiryAt: number;
  userBoostExpiryAt: number;
  isRecvMatching: boolean;
  isIgnoredByReceiver: boolean;
  giftValue?: number;
  isEnded?: boolean;
  isExpired?: boolean;
  roomJid?: string;

  constructor() {
    this.id = 0;
    this.folders = [];
    this.pinned = false;
    this.chatted = false;
    this.unreadCount = 0;
    this.lastBuddySentTime = 0;
    this.lastUserSentTime = 0;
    this.message = new chatMessage();
    this.peerVcard = new userModel() as unknown as IPeerVcard;
    this.timestamp = 0;
    this.isActive = false;
    this.status = 'active';
    this.isGroup = false;
    this.memberCount = 0;
    this.isNewInvitation = false;
    this.eventId = 0;
    this.isIgnored = false;
    this.expiredTime = -1;
    this.isDelete = false;
    this.hasUnreadGifts = false;
    this.memberLevel = MEMBER_TYPES.GUEST;
    this.giftLevel = 1;
    this.isInboxRetention = true;
    this.buddyBoostExpiryAt = 0;
    this.userBoostExpiryAt = 0;
    this.isRecvMatching = false;
    this.isIgnoredByReceiver = false;
  }

  static mappingObject(_conver: any): IConversation {
    let conversation = Object.assign(new Conversation(), _conver);
    const isRecvMatching = _conver.isMatching ? _conver.isMatching : false;
    conversation.pinned = _conver.pinned || false;
    conversation.folders = _conver.folders;
    conversation.id = _conver.id || '';
    conversation.unreadCount = _conver.unreadCount || 0;
    conversation.lastBuddySentTime = _conver.lastBuddySentTime ? _conver.lastBuddySentTime : null;
    conversation.lastUserSentTime = _conver.lastUserSentTime ? _conver.lastUserSentTime : null;
    conversation.message = getChatMessageModel(_conver.message);
    conversation.timestamp = _conver.timestamp || _conver.ts || conversation.message.time;
    conversation.memberLevel = _conver.memberLevel ? _conver.memberLevel : 1;
    conversation.isRecvMatching = isRecvMatching;
    conversation.isActive = _conver.isActive;
    conversation.isDelete = _conver.isDelete;
    conversation.isIgnored = _conver.isIgnored || false;
    conversation.isIgnoredByReceiver = _conver.isIgnored && conversation.message && conversation.message.bySelf && !conversation.isActive;
    conversation.isGroup = _conver.isGroup || false;
    conversation.isNewInvitation = !_conver.isActive;
    conversation.isEnded = _conver.IsEnded || false;
    conversation.isExpired = _conver.IsExpired || false;
    conversation.memberCount = _conver.memberCount || 0;
    conversation.hasUnreadGifts = _conver.hasUnreadGifts || false;
    conversation.giftLevel = _conver.giftLevel || 1;
    conversation.giftValue = _conver.giftValue || 0;
    conversation.buddyBoostExpiryAt = _conver?.buddyBoostExpiryAt || 0;
    conversation.userBoostExpiryAt = _conver?.userBoostExpiryAt || 0;
    conversation.isInboxRetention = utils.hasOwnProperty(_conver, 'isInboxRetention') ? _conver.isInboxRetention : true;
    if (conversation.isGroup) {
      conversation.peerVcard = {
        isGroup: true,
        userId: _conver.id,
        xmppId: _conver.roomJid,
        id: _conver.id,
        avatarUrl: _conver.coverUrl || icMessGroup,
        isExistsAvatar: !!_conver.coverUrl,
        displayName: _conver.subject || 'St. Patricks Swinger Fun (Demo)',
        isExpired: _conver.IsExpired || false,
        isMatch: _conver.isMatching || false,
      };
      conversation.to = messageUtils.removePrefixAndSuffix(_conver.roomJid);
      conversation.eventId = _conver.refObjId || 0;
      conversation.expiredTime = _conver.expiredTime;
    } else {
      conversation.peerVcard = _conver.peerVcard ? userModel.mappingObject(_conver.peerVcard, _conver.memberLevel, isRecvMatching) : null;
    }

    conversation = cleanObjectKeys(Object.keys(new Conversation()), conversation);
    return conversation;
  }

  static mappingConversationFromGroupDetail(groupDetail: GroupDetail): Conversation {
    let conversation = Object.assign(new Conversation(), groupDetail);
    if (groupDetail) {
      conversation.eventId = groupDetail.refObjType === 'event' ? groupDetail.refObjId : 0;
      conversation.expiredTime = groupDetail.expiredAt;
      conversation.folders = groupDetail.folders || [];
      conversation.isDelete = false;
      conversation.isGroup = true;
      conversation.isIgnored = null;
      conversation.isNewInvitation = !groupDetail.isActive;
      conversation.isRecvMatching = groupDetail.isMatching ? groupDetail.isMatching : false;
      conversation.lastBuddySentTime = groupDetail.ts;
      conversation.lastUserSentTime = groupDetail.ts;
      conversation.memberCount = groupDetail.roomMembers ? groupDetail.roomMembers.length : 0;
      conversation.memberLevel = groupDetail.memberLevel ? groupDetail.memberLevel : 1;
      conversation.message = getChatMessageModel({
        body: groupDetail.body,
        expts: groupDetail.body['expts'],
        from: groupDetail.senderJid,
        type: 'groupchat',
      });
      conversation.peerVcard = {
        isGroup: true,
        xmppId: groupDetail.roomJid,
        roomId: groupDetail.roomId,
        userId: groupDetail.id,
        roomName: groupDetail.roomName,
        id: groupDetail.id,
        avatarUrl: groupDetail.coverUrl || icMessGroup,
        isExistsAvatar: !!groupDetail.coverUrl,
        displayName: groupDetail.subject || 'St. Patricks Swinger Fun (Demo)',
        isExpired: groupDetail.isExpired || false,
        isMatch: true,
      } as IPeerVcard;
      conversation.pinned = false;
      conversation.status = 'Active';
      conversation.timestamp = groupDetail.ts;
    }
    return conversation;
  }

  static mappingObjects(_convers: any[]): IConversation[] {
    const convers: IConversation[] = [];
    _convers.forEach(_conver => {
      const conversation = Conversation.mappingObject(_conver);
      convers.push(conversation);
    });
    return convers;
  }
}
